import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { FloatingFocusManager, FloatingPortal, autoUpdate, flip, offset, safePolygon, shift, useDismiss, useFloating, useFocus, useHover, useInteractions, useRole, useTransitionStyles, } from '@floating-ui/react';
/**
 * Renders a popover component, typically used as an interactive tooltip, stays open when hovering over the floating element
 * and the reference element, unlike the Tooltip component which goes away when the user is no longer hovering over the
 * reference element. Wrap it around the element you would like to have the tooltip appear on (reference element).
 *
 * @component
 * @param {Object} props - The component props.
 * @param {ReactNode} props.children - The content of the popover.
 * @param {string} props.text - The text to display in the popover.
 * @param {string} props.variant - The variant of the popover (e.g., 'light', 'dark').
 * @param {Array<Function>} props.extraMiddleware - Additional middleware functions for the popover.
 * @param {Object} props.floatProps - Floating properties for the popover.
 * @returns {JSX.Element} The rendered popover component.
 */
export const Popover = ({ children, text, variant, extraMiddleware, floatProps }) => {
    const [isOpen, setOpen] = useState(false);
    const { refs, floatingStyles, context } = useFloating({
        open: isOpen,
        onOpenChange: setOpen,
        whileElementsMounted: autoUpdate,
        placement: 'right',
        middleware: [
            offset(floatProps?.offset || 10),
            flip({
                crossAxis: true,
                fallbackAxisSideDirection: 'end',
                padding: 5,
                ...floatProps?.flip,
            }),
            shift({ padding: 5, ...floatProps?.shift }),
            ...(extraMiddleware || []),
        ],
    });
    const { isMounted, styles } = useTransitionStyles(context);
    const interactions = [
        useHover(context, {
            // delay: 500,
            handleClose: safePolygon({ blockPointerEvents: true }),
        }),
        useFocus(context),
        useDismiss(context),
        useRole(context, { role: 'menu' }),
    ];
    const { getReferenceProps, getFloatingProps } = useInteractions(interactions);
    return (_jsxs(_Fragment, { children: [_jsx("span", { ref: refs.setReference, ...getReferenceProps(), children: children }), isMounted && text && (_jsx(FloatingPortal, { children: _jsx(FloatingFocusManager, { context: context, children: _jsx("div", { className: `pointer-events-none z-[1500] backdrop-blur ${variant === 'light' ? 'bg-sourgum-greyblue-900/25' : 'bg-sourgum-greyblue-900/75'} -left-1/2 top-full w-max max-w-xs rounded-md px-4 py-2 text-xs text-white transition-opacity duration-300 ease-in-out`, ref: refs.setFloating, style: {
                            ...floatingStyles,
                            ...styles,
                        }, onClick: (e) => e.stopPropagation(), ...getFloatingProps(), children: text }) }) }))] }));
};
//# sourceMappingURL=Popover.js.map